// UI helpers shared by the single-row and bulk Zebra print flows.

import {BrowserPrintError, getRememberedPrinterUid} from './zebraPrint';

/** Prefer the printer the user last printed to, then Browser Print's default, then the first available. */
export function preferredPrinterUid(devices, defaultUid) {
  const remembered = getRememberedPrinterUid();
  if (remembered && devices.some(d => d.uid === remembered)) {
    return remembered;
  }
  if (defaultUid && devices.some(d => d.uid === defaultUid)) {
    return defaultUid;
  }
  return devices[0].uid;
}

/** The Browser Print device name. The backend parses a DPI token from it to scale the label. */
export function deviceLabel(device) {
  return device.name || device.uid || 'Printer';
}

/** User-facing message for a print failure. context is 'row' or 'bulk'. */
export function describeError(err, context = 'row') {
  if (err instanceof BrowserPrintError) {
    return err.message;
  }
  // The api client throws the raw fetch Response on a non-2xx status.
  if (err && typeof err.status === 'number') {
    const bulk = context === 'bulk';
    switch (err.status) {
      case 400:
        return bulk
          ? 'These labels can\'t be printed. The form needs a label layout and a weight (scale) question.'
          : 'This row can\'t be printed. Its form needs a label layout and a weight (scale) question.';
      case 403:
        return 'You don\'t have permission to print labels for this form.';
      case 404:
        return bulk ? 'This form could not be found.' : 'This row could not be found.';
      default:
        return bulk
          ? 'The server could not render the labels. Please try again.'
          : 'The server could not render the label. Please try again.';
    }
  }
  return 'Something went wrong while printing. Please try again.';
}

/**
 * describeError, except a 400 shows the server's own reason when it sent one (e.g. which answer the row
 * is missing), since a 400 has several distinct causes the generic message can't tell apart.
 */
export async function describeErrorWithReason(err, context = 'row') {
  if (err && (err.status === 400 || err.status === 409) && typeof err.json === 'function') {
    try {
      const body = await err.json();
      if (body && body.message) {
        return body.message;
      }
    }
    catch (e) {
      // Not a JSON body; fall back to the generic message.
    }
  }
  return describeError(err, context);
}
